import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Gift, Bell, Tag } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { cn } from '../../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Newsletter subscription popup
 */
export const NewsletterPopup = ({ show, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [preferences, setPreferences] = useState(['novedades']);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        name,
        preferences
      });
      setSuccess(true);
      toast.success('¡Suscripción completada!');
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      toast.error('Error al suscribirse');
    } finally {
      setLoading(false);
    }
  };

  const togglePreference = (pref) => {
    setPreferences(prev => 
      prev.includes(pref) 
        ? prev.filter(p => p !== pref)
        : [...prev, pref]
    );
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={e => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-[#0047FF] to-[#8B5CF6] p-6 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Gift className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">¡Únete a nuestra comunidad!</h2>
                <p className="text-sm text-white/80">Ofertas exclusivas y novedades gaming</p>
              </div>
            </div>
          </div>

          {success ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="text-lg font-semibold dark:text-white">¡Gracias por suscribirte!</h3>
              <p className="text-sm text-[#52525B] dark:text-gray-400 mt-2">
                Pronto recibirás las mejores ofertas en tu email
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>

                {/* Preferences */}
                <div>
                  <p className="text-sm font-medium mb-2 dark:text-white">¿Qué te interesa?</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'novedades', label: 'Novedades', icon: Bell },
                      { id: 'promociones', label: 'Promociones', icon: Tag },
                      { id: 'descuentos', label: 'Códigos descuento', icon: Gift }
                    ].map((pref) => (
                      <button
                        key={pref.id}
                        type="button"
                        onClick={() => togglePreference(pref.id)}
                        className={cn(
                          'flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors',
                          preferences.includes(pref.id)
                            ? 'bg-[#0047FF] text-white'
                            : 'bg-[#F5F5F7] dark:bg-gray-700 text-[#52525B] dark:text-gray-300'
                        )}
                      >
                        <pref.icon className="w-3 h-3" />
                        {pref.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full mt-6 bg-[#0047FF] hover:bg-[#003BCC]"
              >
                {loading ? 'Suscribiendo...' : 'Suscribirme gratis'}
              </Button>

              <p className="text-xs text-center text-[#A1A1AA] mt-4">
                Al suscribirte aceptas recibir comunicaciones comerciales. 
                Puedes darte de baja en cualquier momento.
              </p>
            </form>
          )}

          {/* WhatsApp Groups */}
          <div className="bg-[#F5F5F7] dark:bg-gray-700 p-4 border-t border-[#E5E7EB] dark:border-gray-600">
            <p className="text-sm font-medium mb-2 dark:text-white">También en WhatsApp:</p>
            <div className="flex gap-2">
              <a
                href="https://chat.whatsapp.com/promociones"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center py-2 bg-[#25D366] text-white text-sm rounded-lg hover:bg-[#20BD5A] transition-colors"
              >
                Grupo Promociones
              </a>
              <a
                href="https://chat.whatsapp.com/descuentos"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center py-2 bg-[#25D366] text-white text-sm rounded-lg hover:bg-[#20BD5A] transition-colors"
              >
                Grupo Descuentos
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to manage newsletter popup
 */
export const useNewsletterPopup = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show popup on admin pages
    const isAdminPage = window.location.pathname.startsWith('/admin');
    if (isAdminPage) return;

    // Show popup after 30 seconds if not shown before
    const hasSeenPopup = localStorage.getItem('newsletter_popup_seen');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        // Double-check we're not on admin page when timer fires
        if (!window.location.pathname.startsWith('/admin')) {
          setShow(true);
          localStorage.setItem('newsletter_popup_seen', 'true');
        }
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, []);

  const showPopup = () => setShow(true);
  const hidePopup = () => setShow(false);

  return { show, showPopup, hidePopup };
};

export default NewsletterPopup;
